import React from 'react';
import { toast, type ToastOptions } from 'react-toastify';
import { Toast } from '@packages/ui/composites/Toast';
import { showApiErrorToast } from './showApiErrorToast';

export interface NotifyPromiseMessages {
  pending: string;
  success: string;
  error?: string;
}

/**
 * Unified toast notification API.
 *
 * Usage:
 *   import { notify } from '@packages/shared/utils';
 *
 *   // Simple notifications
 *   notify.success('Done');
 *   notify.error('Failed', { autoClose: 30000 });
 *
 *   // Async operations with loading → success/error lifecycle
 *   await notify.promise(
 *     async () => {
 *       const res = await api.exportParticipants(payload);
 *       downloadBlob(res);
 *     },
 *     { pending: 'Exporting participants...', success: 'Export completed' }
 *   );
 */
export const notify = {
  success(message: string, options?: ToastOptions) {
    return toast.success(<Toast label='Success' message={message} />, options);
  },

  error(message: string, options?: ToastOptions) {
    return toast.error(<Toast label='Error' message={message} />, options);
  },

  info(message: string, options?: ToastOptions) {
    return toast.info(<Toast label='Info' message={message} />, options);
  },

  /** Show a persistent info toast (no auto-close). Returns a toastId for later update/dismiss. */
  pending(message: string, options?: ToastOptions) {
    return toast.info(<Toast label='Info' message={message} />, {
      autoClose: false,
      ...options,
    });
  },

  /** Update an existing toast by ID with a new type and message. */
  update(
    toastId: ReturnType<typeof toast.info>,
    type: 'success' | 'error' | 'info',
    message: string,
    options?: ToastOptions
  ) {
    const labelMap = { success: 'Success', error: 'Error', info: 'Info' } as const;
    const typeMap = { success: toast.TYPE.SUCCESS, error: toast.TYPE.ERROR, info: toast.TYPE.INFO } as const;
    toast.update(toastId, {
      render: <Toast label={labelMap[type]} message={message} />,
      type: typeMap[type],
      autoClose: options?.autoClose,
      ...options,
    });
  },

  /** Dismiss a toast by ID. */
  dismiss(toastId: ReturnType<typeof toast.info>) {
    toast.dismiss(toastId);
  },

  /**
   * Wraps an async operation with a loading → success/error toast lifecycle.
   *
   * Shows a pending toast immediately, then transitions the SAME toast to
   * success or error when the promise resolves/rejects.
   *
   * On error the pending toast is dismissed and `showApiErrorToast` handles
   * the error (toast, error modal, or suppression depending on the error shape).
   * The error is swallowed (not re-thrown). Check for `undefined` if you need
   * the resolved value:
   *
   *   const data = await notify.promise(
   *     () => fetchData(),
   *     { pending: 'Loading...', success: 'Loaded' }
   *   );
   *   if (data != null) { ... }
   */
  async promise<T>(
    fn: () => Promise<T>,
    messages: NotifyPromiseMessages,
    options?: ToastOptions
  ): Promise<T | undefined> {
    const toastId = toast.info(<Toast label='Info' message={messages.pending} />, {
      autoClose: false,
      ...options,
    });

    try {
      const result = await fn();
      toast.update(toastId, {
        render: <Toast label='Success' message={messages.success} />,
        type: toast.TYPE.SUCCESS,
        autoClose: options?.autoClose,
      });
      return result;
    } catch (error) {
      toast.dismiss(toastId);
      showApiErrorToast(error);
      return undefined;
    }
  },
};
